/**
 * SettingsPage — farm configuration hub.
 *
 * Tabs: Farm Profile · Team · Billing · Appearance · Notifications · Security
 *
 * "Farm Profile" is the primary tab — sets up the farm entity itself:
 * name, address, timezone, description, contact info, size, logo.
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Building2, Users, CreditCard, Palette, Bell, Shield,
  Key, Save, MapPin, Globe, Phone, Mail, Clock, Ruler,
  Image, CheckCircle2, AlertCircle, ChevronRight,
  Copy, Bot,
  Calendar, Download,
} from 'lucide-react'
import { api, tenantApi, calendarApi, aiInsightsApi, type TenantProfile, type TenantMember } from '../lib/api'
import { useAuthStore } from '../lib/store'
import { useAccessibility } from '../lib/accessibility'
import { pushSupported, pushPermission, isPushEnabled, enablePush, disablePush } from '../lib/push'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '../components/shared/LanguageSwitcher'
import { ConsentStatusBadge } from '../components/shared/AIConsentModal'

// ── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  bg:     'var(--color-bg)',
  card:   'var(--color-bg-card)',
  card2:  '#1A2212',
  border: 'var(--color-border)',
  amber:  'var(--color-primary)',
  green:  '#2D6A2F',
  text:   'var(--color-text)',
  muted:  'var(--color-text-muted)',
  faint:  'var(--color-text-faint)',
  danger: '#F87171',
  success:'#4ADE80',
}

// ── Shared styles helpers ──────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  fontSize: 11, color: C.muted, marginBottom: 5, display: 'block',
  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
}
const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--color-bg-hover)', border: `1px solid ${C.border}`,
  borderRadius: 7, padding: '9px 12px', color: C.text, fontSize: 13, outline: 'none',
  boxSizing: 'border-box',
}
const textareaStyle: React.CSSProperties = {
  ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'inherit',
}
function btnStyle(variant: 'primary' | 'ghost' | 'danger'): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 500,
    background: variant === 'primary' ? C.amber
               : variant === 'danger'  ? 'rgba(248,113,113,0.12)'
               : 'rgba(255,255,255,0.06)',
    color: variant === 'primary' ? 'var(--color-bg)'
         : variant === 'danger'  ? C.danger
         : C.muted,
  }
}

// ── Billing / security row shapes ───────────────────────────────────────────────
interface Invoice { id: string; created_at: string; amount_cents?: number; status: string; pdf_url?: string }
interface SessionInfo { id: string; user_agent?: string; ip_address?: string; last_used_at: string; is_current?: boolean }

// ── Tab definitions ────────────────────────────────────────────────────────────
type Tab = 'farm' | 'team' | 'billing' | 'appearance' | 'notifications' | 'security' | 'ai'

const TABS: { id: Tab; label: string; icon: React.ElementType; desc: string }[] = [
  { id: 'farm',          label: 'Farm Profile',    icon: Building2,  desc: 'Name, address, contact info' },
  { id: 'team',          label: 'Team',            icon: Users,      desc: 'Members and invitations'     },
  { id: 'billing',       label: 'Billing',         icon: CreditCard, desc: 'Plan, usage, invoices'       },
  { id: 'appearance',    label: 'Appearance',      icon: Palette,    desc: 'Theme, font, accessibility'  },
  { id: 'notifications', label: 'Notifications',   icon: Bell,       desc: 'Email alert preferences'     },
  { id: 'security',      label: 'Security',        icon: Shield,     desc: 'Password, 2FA, sessions'     },
  { id: 'ai',            label: 'AI',              icon: Bot,        desc: 'AI consent and data use'     },
]

const US_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Puerto_Rico',
]

// Swatch colours MUST match each theme's --color-primary in design-system.css so
// the picker chip previews exactly the accent you get.
const THEMES = [
  { key: 'mango',      label: 'Mango',      color: 'var(--color-primary)' },
  { key: 'apple',      label: 'Apple',      color: '#E8394A' },
  { key: 'orange',     label: 'Citrus',     color: '#FF6B2B' },
  { key: 'watermelon', label: 'Watermelon', color: '#FF4D6A' },
  { key: 'palm',       label: 'Palm',       color: '#00BFA5' },
  { key: 'nursery',    label: 'Nursery',    color: '#8FBD6B' },
  { key: 'floral',     label: 'Floral',     color: '#C77DD1' },
  { key: 'farm',       label: 'Farmhouse',  color: '#D4624B' },
  { key: 'peach',      label: 'Peach',      color: '#FFAB76' },
  { key: 'taupe',      label: 'Taupe',      color: '#C8B89A' },
]

// ── Page shell ─────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { role } = useAuthStore()
  const [tab, setTab] = useState<Tab>('farm')

  return (
    <main id="main-content" style={{ padding: '28px 32px', maxWidth: 980, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0, fontFamily: '"Syne", system-ui, sans-serif' }}>
          Settings
        </h1>
        <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>
          Configure your farm, manage your team, and personalise Grafty
        </p>
      </div>

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Sidebar nav */}
        <nav style={{ width: 190, flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 9,
                  padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: tab === id ? 600 : 400,
                  color: tab === id ? C.amber : C.muted,
                  background: tab === id ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'transparent',
                  textAlign: 'left', width: '100%',
                }}
              >
                <Icon size={14} style={{ flexShrink: 0 }} />
                {label}
                {tab === id && <ChevronRight size={12} style={{ marginLeft: 'auto', opacity: 0.5 }} />}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {tab === 'farm'          && <FarmProfileTab role={role} />}
          {tab === 'team'          && <TeamTab role={role} />}
          {tab === 'billing'       && <BillingTab />}
          {tab === 'appearance'    && <AppearanceTab />}
          {tab === 'notifications' && <NotificationsTab />}
          {tab === 'security'      && <SecurityTab />}
          {tab === 'ai'            && <AITab />}
        </div>
      </div>
    </main>
  )
}

// ── Shared Card ───────────────────────────────────────────────────────────────

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
      <div style={{ marginBottom: 18 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0 }}>{title}</h3>
        {subtitle && <p style={{ fontSize: 12, color: C.muted, margin: '3px 0 0' }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}

function SaveRow({ isPending, isSuccess, isError, onSave, label = 'Save Changes' }: {
  isPending: boolean; isSuccess: boolean; isError: boolean; onSave: () => void; label?: string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
      <button style={btnStyle('primary')} onClick={onSave} disabled={isPending}>
        <Save size={13} />
        {isPending ? 'Saving…' : label}
      </button>
      {isSuccess && (
        <span style={{ fontSize: 12, color: C.success, display: 'flex', alignItems: 'center', gap: 4 }}>
          <CheckCircle2 size={13} /> Saved
        </span>
      )}
      {isError && (
        <span style={{ fontSize: 12, color: C.danger, display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertCircle size={13} /> Failed to save
        </span>
      )}
    </div>
  )
}

// ── Farm Profile Tab ──────────────────────────────────────────────────────────

function FarmProfileTab({ role }: { role: string | null }) {
  const qc = useQueryClient()
  const canEdit = role === 'owner' || role === 'admin' || role === 'superuser'

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['tenant-profile'],
    queryFn: () => tenantApi.get().then(r => r.data),
  })

  const [form, setForm] = useState<Partial<TenantProfile>>({})
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (tenant && !initialized) {
      setForm({
        name:             tenant.name             ?? '',
        description:      tenant.description      ?? '',
        address:          tenant.address          ?? '',
        city:             tenant.city             ?? '',
        state:            tenant.state            ?? '',
        zip:              tenant.zip              ?? '',
        country:          tenant.country          ?? 'US',
        phone:            tenant.phone            ?? '',
        email:            tenant.email            ?? '',
        website:          tenant.website          ?? '',
        timezone:         tenant.timezone         ?? 'America/New_York',
        size_acres:       tenant.size_acres,
        logo_url:         tenant.logo_url         ?? '',
        established_year: tenant.established_year,
        vineyard_mode:    tenant.vineyard_mode    ?? false,
      })
      setInitialized(true)
    }
  }, [tenant, initialized])

  const setF = (k: keyof TenantProfile, v: unknown) => setForm(f => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: () => tenantApi.update(form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-profile'] }),
  })

  if (isLoading) {
    return <div style={{ padding: '60px 0', textAlign: 'center', color: C.faint }}>Loading farm profile…</div>
  }

  const fieldProps = (k: keyof TenantProfile): React.InputHTMLAttributes<HTMLInputElement> => ({
    style: inputStyle,
    value: (form[k] as string) ?? '',
    onChange: e => setF(k, e.target.value),
    disabled: !canEdit,
  })

  return (
    <div>
      {/* Identity */}
      <Card title="Farm Identity" subtitle="How your farm appears to your team and on public pages">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Farm Name *</label>
            <input {...fieldProps('name')} placeholder="Sunshine Mango Farm" style={{ ...inputStyle, fontSize: 15, fontWeight: 600 }} />
          </div>
          <div>
            <label style={labelStyle}>Established Year</label>
            <input type="number" {...fieldProps('established_year')}
              value={form.established_year ?? ''}
              onChange={e => setF('established_year', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="2005" min={1800} max={new Date().getFullYear()} />
          </div>
          <div>
            <label style={labelStyle}><Ruler size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Farm Size (acres)</label>
            <input type="number" {...fieldProps('size_acres')}
              value={form.size_acres ?? ''}
              onChange={e => setF('size_acres', e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="12.5" min={0} step={0.1} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Description / About</label>
            <textarea
              style={{ ...textareaStyle, ...(!canEdit ? { opacity: 0.6 } : {}) }}
              value={form.description ?? ''}
              onChange={e => setF('description', e.target.value)}
              placeholder="Family-run mango orchard specializing in rare tropical varieties…"
              disabled={!canEdit}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}><Image size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Logo URL</label>
            <input {...fieldProps('logo_url')} placeholder="https://example.com/logo.png" />
            {form.logo_url && (
              <div style={{ marginTop: 8 }}>
                <img src={form.logo_url} alt="Logo preview" style={{ height: 48, borderRadius: 6, border: `1px solid ${C.border}`, objectFit: 'contain', background: C.card2 }} onError={e => (e.currentTarget.style.display = 'none')} />
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Location */}
      <Card title="Location" subtitle="Farm address — used to centre your farm map and weather data">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}><MapPin size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Street Address</label>
            <input {...fieldProps('address')} placeholder="1234 Grove Road" />
          </div>
          <div>
            <label style={labelStyle}>City</label>
            <input {...fieldProps('city')} placeholder="Homestead" />
          </div>
          <div>
            <label style={labelStyle}>State / Province</label>
            <input {...fieldProps('state')} placeholder="FL" />
          </div>
          <div>
            <label style={labelStyle}>ZIP / Postal Code</label>
            <input {...fieldProps('zip')} placeholder="33030" />
          </div>
          <div>
            <label style={labelStyle}>Country</label>
            <input {...fieldProps('country')} placeholder="US" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}><Clock size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Timezone</label>
            <select
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={form.timezone ?? 'America/New_York'}
              onChange={e => setF('timezone', e.target.value)}
              disabled={!canEdit}
            >
              {US_TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz.replace('America/', '').replace('Pacific/', 'Pacific/').replace(/_/g, ' ')}</option>
              ))}
              <option value="Europe/London">Europe / London</option>
              <option value="Europe/Berlin">Europe / Berlin</option>
              <option value="Australia/Sydney">Australia / Sydney</option>
              <option value="Asia/Manila">Asia / Manila</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Contact */}
      <Card title="Contact Information" subtitle="Public contact details shown on your marketing site">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}><Phone size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Phone</label>
            <input {...fieldProps('phone')} placeholder="+1 305 555 0100" type="tel" />
          </div>
          <div>
            <label style={labelStyle}><Mail size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Contact Email</label>
            <input {...fieldProps('email')} placeholder="hello@myfarm.com" type="email" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}><Globe size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Website</label>
            <input {...fieldProps('website')} placeholder="https://myfarm.com" type="url" />
          </div>
        </div>
      </Card>

      {/* Cultivation mode */}
      <Card title="Cultivation Mode" subtitle="Vineyard mode relabels “Variety” as “Vine variety” and adds trellis + row/vine spacing to blocks">
        <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: canEdit ? 'pointer' : 'default' }}>
          <input
            type="checkbox"
            checked={!!form.vineyard_mode}
            onChange={e => setF('vineyard_mode', e.target.checked)}
            disabled={!canEdit}
            style={{ width: 18, height: 18, accentColor: 'var(--color-primary)', cursor: canEdit ? 'pointer' : 'default' }}
          />
          <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>
            Vineyard mode
            <span style={{ display: 'block', fontSize: 12, color: C.faint, fontWeight: 400, marginTop: 2 }}>
              For grape growers — tracks trellis type, row spacing, and vine spacing per block.
            </span>
          </span>
        </label>
      </Card>

      {canEdit && (
        <SaveRow
          isPending={save.isPending}
          isSuccess={save.isSuccess}
          isError={save.isError}
          onSave={() => save.mutate()}
        />
      )}
      {!canEdit && (
        <p style={{ fontSize: 12, color: C.faint, marginTop: 8 }}>
          Only farm owners and admins can edit the farm profile.
        </p>
      )}

      <Card title="Setup Guide" subtitle="Revisit the new-farm setup checklist any time">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('grafty:open-setup'))}
          style={{ padding: '9px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.text, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
        >
          Re-open setup guide
        </button>
      </Card>
    </div>
  )
}

// ── Team Tab ──────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  owner:               { label: 'Owner',               color: C.amber   },
  admin:               { label: 'Admin',               color: '#C77DFF' },
  zone_manager:        { label: 'Zone Manager',        color: '#60A5FA' },
  irrigation_manager:  { label: 'Irrigation Mgr',      color: '#34D399' },
  irrigation_operator: { label: 'Irrigation Operator', color: '#86EFAC' },
  farm_hand:           { label: 'Farm Hand',           color: 'var(--color-text-muted)' },
  viewer:              { label: 'Viewer',              color: 'var(--color-text-faint)' },
}

function RoleBadge({ role }: { role: string }) {
  const info = ROLE_LABELS[role] ?? { label: role.replace(/_/g, ' '), color: C.faint }
  return (
    <span style={{
      fontSize: 11, padding: '3px 9px', borderRadius: 99, fontWeight: 600,
      background: info.color + '1A', color: info.color, border: `1px solid ${info.color}33`,
      whiteSpace: 'nowrap',
    }}>
      {info.label}
    </span>
  )
}

function TeamTab({ role }: { role: string | null }) {
  const qc = useQueryClient()
  const canManage = role === 'owner' || role === 'admin' || role === 'superuser'

  // ── Members ──────────────────────────────────────────────────────────────
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => tenantApi.members().then(r => r.data),
  })
  const members: TenantMember[] = membersData?.members ?? []

  const removeMember = useMutation({
    mutationFn: (userId: string) => tenantApi.removeMember(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-members'] }),
  })

  // ── Email invite ──────────────────────────────────────────────────────────
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole,  setInviteRole]  = useState('farm_hand')
  const [inviteSent,  setInviteSent]  = useState(false)

  const invite = useMutation({
    mutationFn: () => tenantApi.invite(inviteEmail, inviteRole),
    onSuccess: () => {
      setInviteEmail('')
      setInviteSent(true)
      setTimeout(() => setInviteSent(false), 4000)
      qc.invalidateQueries({ queryKey: ['team-members'] })
    },
  })

  return (
    <div>

      {/* ── Email invite (adds the person to this farm with the chosen role) ── */}
      {canManage && (
        <Card
          title="Invite by Email"
          subtitle="Sends a direct invite — when they accept, they're added automatically with the chosen role"
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>Email Address</label>
              <input
                style={inputStyle} type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && inviteEmail && invite.mutate()}
              />
            </div>
            <div>
              <label style={labelStyle}>Role</label>
              <select
                style={{ ...inputStyle, width: 155, cursor: 'pointer' }}
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
              >
                <option value="farm_hand">Farm Hand</option>
                <option value="viewer">Viewer</option>
                <option value="zone_manager">Zone Manager</option>
                <option value="irrigation_operator">Irrigation Operator</option>
                <option value="irrigation_manager">Irrigation Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              style={btnStyle('primary')}
              onClick={() => invite.mutate()}
              disabled={!inviteEmail || invite.isPending}
            >
              <Mail size={13} />
              {invite.isPending ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
          {inviteSent && (
            <p style={{ fontSize: 12, color: C.success, marginTop: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
              <CheckCircle2 size={13} /> Invite sent — they'll get an email to set up their account, or are added now if they already have one.
            </p>
          )}
          {invite.isError && (
            <p style={{ fontSize: 12, color: C.danger, marginTop: 10 }}>Failed to send invite. Try again.</p>
          )}
        </Card>
      )}

      {/* ── Active members ── */}
      <Card title="Team Members" subtitle={`${members.length} active member${members.length !== 1 ? 's' : ''}`}>
        {membersLoading && <p style={{ color: C.faint, fontSize: 13 }}>Loading…</p>}
        {!membersLoading && members.length === 0 && (
          <p style={{ color: C.muted, fontSize: 13 }}>No active members yet — use the invite options above to add people.</p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {members.map(m => {
            const primaryRole = m.roles?.[0] ?? 'viewer'
            return (
              <div key={m.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '11px 14px', background: C.card2, borderRadius: 9,
                border: `1px solid ${C.border}`,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.display_name || m.email}
                  </div>
                  {m.display_name && <div style={{ fontSize: 12, color: C.muted }}>{m.email}</div>}
                  {m.status === 'invited' && (
                    <div style={{ fontSize: 11, color: C.amber, marginTop: 2 }}>✉ Invite pending…</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  <RoleBadge role={primaryRole} />
                  {canManage && primaryRole !== 'owner' && (
                    <button
                      onClick={() => { if (confirm(`Remove ${m.display_name || m.email} from the farm?`)) removeMember.mutate(m.id) }}
                      style={{ ...btnStyle('ghost'), padding: '4px 10px', fontSize: 11 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </Card>

      {/* ── Role reference ── */}
      <Card title="Role Reference">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { role: 'farm_hand',          desc: 'Log care events, harvests, observations' },
            { role: 'zone_manager',        desc: 'Manage zones, care templates, plants' },
            { role: 'irrigation_operator', desc: 'Run zones manually, view schedules' },
            { role: 'irrigation_manager',  desc: 'Full irrigation config & scheduling' },
            { role: 'viewer',              desc: 'Read-only access to all data' },
            { role: 'admin',               desc: 'Full access except billing & ownership' },
          ].map(({ role: r, desc }) => {
            const info = ROLE_LABELS[r] ?? { label: r, color: C.faint }
            return (
              <div key={r} style={{ display: 'flex', gap: 8, padding: '8px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 7 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: info.color, whiteSpace: 'nowrap', paddingTop: 1 }}>{info.label}</span>
                <span style={{ fontSize: 11, color: C.faint }}>{desc}</span>
              </div>
            )
          })}
        </div>
      </Card>
    </div>
  )
}

// ── Billing Tab ───────────────────────────────────────────────────────────────

function BillingTab() {
  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get('/billing/subscription').then(r => r.data).catch(() => null),
  })
  const { data: invoices = [] } = useQuery<Invoice[]>({
    queryKey: ['invoices'],
    queryFn: () => api.get('/billing/invoices').then(r => (r.data ?? []) as Invoice[]).catch(() => []),
  })

  const planColors: Record<string, string> = { seedling: '#74C69D', grower: C.amber, estate: '#C77DFF' }
  const plan = sub?.plan ?? 'seedling'
  const planColor = planColors[plan] ?? C.amber
  const planPrices: Record<string, string> = { seedling: 'Free', grower: '$99 / year', estate: '$199 / year' }

  return (
    <div>
      <Card title="Current Plan">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: planColor, textTransform: 'capitalize' }}>
                {plan}
              </span>
              <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 99, background: `${planColor}20`, color: planColor, fontWeight: 600 }}>
                {sub?.status ?? 'active'}
              </span>
            </div>
            <div style={{ fontSize: 13, color: C.muted }}>{planPrices[plan] ?? '—'}</div>
          </div>
          {plan !== 'estate' && (
            <button
              style={btnStyle('primary')}
              onClick={() => api.get('/billing/portal').then(r => window.open(r.data?.url)).catch(() => alert('Billing portal not available yet.'))}
            >
              Upgrade Plan
            </button>
          )}
        </div>

        {/* Usage bars */}
        {[
          { label: 'Plants',       used: sub?.usage?.plants,       max: sub?.limits?.plants       },
          { label: 'Harvest rows', used: sub?.usage?.harvest_rows, max: sub?.limits?.harvest_rows },
          { label: 'Team members', used: sub?.usage?.members,      max: sub?.limits?.members      },
        ].map(({ label, used, max }) => max != null && max > 0 && (
          <div key={label} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 5 }}>
              <span>{label}</span>
              <span style={{ color: used != null && max > 0 && (used / max) > 0.95 ? C.danger : C.muted }}>
                {used ?? 0} / {max === -1 ? '∞' : max}
              </span>
            </div>
            <div style={{ height: 5, background: 'var(--color-border)', borderRadius: 3 }}>
              <div style={{
                height: '100%', borderRadius: 3,
                width: `${Math.min(100, ((used ?? 0) / Math.max(max, 1)) * 100)}%`,
                background: ((used ?? 0) / max) > 0.95 ? C.danger : ((used ?? 0) / max) > 0.8 ? C.amber : C.success,
                transition: 'width 0.3s',
              }} />
            </div>
          </div>
        ))}

        {/* Plan comparison */}
        <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, fontSize: 12, color: C.faint }}>
          <strong style={{ color: C.muted }}>Plan comparison:</strong>{' '}
          Seedling — 25 plants · Grower — 250 plants + analytics · Estate — unlimited + all features
        </div>
      </Card>

      <Card title="Billing History">
        {invoices.length === 0 ? (
          <p style={{ color: C.muted, fontSize: 13 }}>No invoices yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Date', 'Amount', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.slice(0, 10).map((inv) => (
                <tr key={inv.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '10px 10px', fontSize: 13, color: C.muted }}>{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '10px 10px', fontSize: 13, color: C.text, fontWeight: 600 }}>${((inv.amount_cents ?? 0) / 100).toFixed(2)}</td>
                  <td style={{ padding: '10px 10px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
                      background: inv.status === 'paid' ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
                      color: inv.status === 'paid' ? C.success : C.danger,
                    }}>{inv.status}</span>
                  </td>
                  <td style={{ padding: '10px 10px' }}>
                    {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: C.amber, textDecoration: 'none' }}>PDF ↗</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}

// ── Appearance Tab ─────────────────────────────────────────────────────────────

// Tenant-admin: the appearance new members start with (they can change their own).
function TenantDefaultAppearance() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['tenant-profile'], queryFn: () => tenantApi.get().then(r => r.data) })
  const save = useMutation({
    mutationFn: (patch: Partial<TenantProfile>) => tenantApi.update({ ...(data as TenantProfile), ...patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-profile'] }),
  })
  const mode  = (data?.default_mode as string) ?? 'dark'
  const theme = (data?.default_theme as string) ?? 'mango'
  return (
    <Card title="Default for new members" subtitle="New team members start with this look — they can change their own anytime">
      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        {([['dark', 'Dark', '🌙'], ['light', 'Light', '☀️']] as const).map(([val, label, icon]) => (
          <button key={val} disabled={!data || save.isPending} onClick={() => save.mutate({ default_mode: val })}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 8px', borderRadius: 10, border: '2px solid', borderColor: mode === val ? 'var(--color-primary)' : C.border, background: mode === val ? 'color-mix(in srgb, var(--color-primary) 12%, transparent)' : 'rgba(255,255,255,0.02)', color: mode === val ? C.text : C.faint, fontWeight: mode === val ? 600 : 400, fontSize: 13, cursor: data ? 'pointer' : 'wait' }}>
            <span aria-hidden>{icon}</span> {label}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {THEMES.map(({ key, label, color }) => (
          <button key={key} disabled={!data || save.isPending} onClick={() => save.mutate({ default_theme: key })}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '12px 8px', borderRadius: 10, border: '2px solid', borderColor: theme === key ? color : C.border, background: theme === key ? `${color}14` : 'rgba(255,255,255,0.02)', cursor: data ? 'pointer' : 'wait' }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: color }} />
            <span style={{ fontSize: 11, color: theme === key ? C.text : C.faint }}>{label}</span>
          </button>
        ))}
      </div>
    </Card>
  )
}

function AppearanceTab() {
  // Route every control through the AccessibilityProvider so it applies LIVE
  // (sets data-theme / --font-size-offset / .reduce-motion / data-high-contrast
  // on <html>) AND persists to /me/preferences. The old local-state + manual PUT
  // saved but never updated the page, so themes appeared not to work.
  const { prefs, updatePref } = useAccessibility()
  const { t: tt } = useTranslation()
  const { role } = useAuthStore()
  const canEditTenant = role === 'owner' || role === 'admin' || role === 'superuser'
  const activeTheme  = prefs.theme
  const fontOffset   = prefs.fontSizeOffset
  const reduceMotion = prefs.reduceMotion
  const highContrast = prefs.highContrast
  const setActiveTheme  = (v: string)  => updatePref('theme', v)
  const setFontOffset   = (v: number)  => updatePref('fontSizeOffset', v)
  const setReduceMotion = (v: boolean) => updatePref('reduceMotion', v)
  const setHighContrast = (v: boolean) => updatePref('highContrast', v)

  return (
    <div>
      <Card title={tt('settings.language', 'Language')} subtitle={tt('settings.language_help', 'Choose the language for the Grafty interface.')}>
        <LanguageSwitcher />
      </Card>

      <Card title="Appearance Mode" subtitle="Light or dark — switch any time (also from the sidebar)">
        <div style={{ display: 'flex', gap: 10 }}>
          {([['dark', 'Dark', '🌙'], ['light', 'Light', '☀️']] as const).map(([val, label, icon]) => (
            <button
              key={val}
              onClick={() => updatePref('mode', val)}
              aria-pressed={prefs.mode === val}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '14px 8px', borderRadius: 10, border: '2px solid',
                borderColor: prefs.mode === val ? 'var(--color-primary)' : C.border,
                background: prefs.mode === val ? 'color-mix(in srgb, var(--color-primary) 12%, transparent)' : 'rgba(255,255,255,0.02)',
                color: prefs.mode === val ? C.text : C.faint, fontWeight: prefs.mode === val ? 600 : 400,
                fontSize: 13, cursor: 'pointer',
              }}
            >
              <span aria-hidden>{icon}</span> {label}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Colour Theme" subtitle="Choose your accent colour — applies across the whole app">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {THEMES.map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => setActiveTheme(key)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                padding: '14px 8px', borderRadius: 10, border: `2px solid`,
                borderColor: activeTheme === key ? color : C.border,
                background: activeTheme === key ? `${color}14` : 'rgba(255,255,255,0.02)',
                cursor: 'pointer',
              }}
            >
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: 11, color: activeTheme === key ? C.text : C.faint, fontWeight: activeTheme === key ? 600 : 400 }}>
                {label}
              </span>
            </button>
          ))}
        </div>
      </Card>

      {canEditTenant && <TenantDefaultAppearance />}

      <Card title="Accessibility">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Font size */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>Font size</div>
              <div style={{ fontSize: 12, color: C.muted }}>Adjusts base text size across the app</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[-1, 0, 1, 2].map(offset => (
                <button
                  key={offset}
                  onClick={() => setFontOffset(offset)}
                  style={{
                    width: 36, height: 36, borderRadius: 7, border: `1px solid`,
                    borderColor: fontOffset === offset ? C.amber : C.border,
                    background: fontOffset === offset ? 'color-mix(in srgb, var(--color-primary) 12%, transparent)' : 'transparent',
                    color: fontOffset === offset ? C.amber : C.muted,
                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  }}
                >
                  {offset === 0 ? 'A' : offset > 0 ? `A+${offset}` : `A${offset}`}
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          {[
            { label: 'Reduce motion',  desc: 'Suppress animations and transitions', val: reduceMotion, set: setReduceMotion },
            { label: 'High contrast',  desc: 'Boost border and text contrast',       val: highContrast, set: setHighContrast },
          ].map(({ label, desc, val, set }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{desc}</div>
              </div>
              <button
                role="switch" aria-checked={val}
                onClick={() => set(!val)}
                style={{
                  width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: val ? C.amber : 'rgba(255,255,255,0.12)',
                  position: 'relative', transition: 'background 0.15s', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: 4, left: val ? 20 : 4,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.15s',
                }} />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Measurement System" subtitle="Units shown across the app — harvest weights, plant sizes, areas">
        <div style={{ display: 'flex', gap: 10 }}>
          {([['imperial', 'Imperial', 'lbs · in · acres · °F'], ['metric', 'Metric', 'kg · cm · ha · °C']] as const).map(([val, label, sub]) => (
            <button
              key={val}
              onClick={() => updatePref('measurementSystem', val)}
              style={{
                flex: 1, padding: '12px 10px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                border: `2px solid ${prefs.measurementSystem === val ? C.amber : C.border}`,
                background: prefs.measurementSystem === val ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'transparent',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>
            </button>
          ))}
        </div>
      </Card>

      <p style={{ fontSize: 12, color: C.muted, marginTop: 14, textAlign: 'center' }}>
        Appearance changes apply instantly and are saved to your profile.
      </p>
    </div>
  )
}

// ── Notifications Tab ─────────────────────────────────────────────────────────

function NotificationsTab() {
  const PREFS = [
    { id: 'permit_expiry',    label: 'Permit expiry alerts',      desc: '30 days before a permit or licence expires' },
    { id: 'frost_warning',    label: 'Frost warnings',            desc: 'When overnight frost risk is detected at your farm' },
    { id: 'task_due',         label: 'Task reminders',            desc: 'Day before an assigned task is due' },
    { id: 'harvest_overdue',  label: 'Overdue care alerts',       desc: 'When recommended care actions are overdue' },
    { id: 'trial_expiry',     label: 'Trial expiry',              desc: '7 days before your analytics trial ends' },
    { id: 'report_ready',     label: 'Scheduled report ready',    desc: 'When a scheduled report is generated' },
    { id: 'low_stock',        label: 'Low inventory alerts',      desc: 'When a supply item drops below reorder point' },
    { id: 'team_invite',      label: 'Team activity',             desc: 'When someone accepts or joins your farm' },
  ]
  const [enabled, setEnabled] = useState<Record<string, boolean>>(
    Object.fromEntries(PREFS.map(p => [p.id, true]))
  )

  const save = useMutation({
    mutationFn: () => api.patch('/me/notification-preferences', enabled).catch(() => {}),
  })

  return (
    <div>
      <Card title="Email Notifications" subtitle="Choose which events send you an email">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
          {PREFS.map(({ id, label, desc }) => (
            <div key={id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{desc}</div>
              </div>
              <button
                role="switch" aria-checked={enabled[id]}
                onClick={() => setEnabled(e => ({ ...e, [id]: !e[id] }))}
                style={{
                  width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: enabled[id] ? C.amber : 'rgba(255,255,255,0.12)',
                  position: 'relative', transition: 'background 0.15s', flexShrink: 0, marginLeft: 16,
                }}
              >
                <div style={{
                  position: 'absolute', top: 4, left: enabled[id] ? 20 : 4,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.15s',
                }} />
              </button>
            </div>
          ))}
        </div>
        <SaveRow isPending={save.isPending} isSuccess={save.isSuccess} isError={save.isError} onSave={() => save.mutate()} label="Save Preferences" />
      </Card>

      <PushNotificationsCard />
      <CalendarSubscribeCard />
    </div>
  )
}

// ── Web push notifications (free, §225) ─────────────────────────────────────────

function PushNotificationsCard() {
  const [supported] = useState(pushSupported())
  const [enabled, setEnabled] = useState(false)
  const [perm, setPerm] = useState(pushPermission())
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    isPushEnabled().then(setEnabled)
  }, [])

  const toggle = async () => {
    setBusy(true)
    setMsg('')
    try {
      if (enabled) {
        await disablePush()
        setEnabled(false)
      } else {
        const r = await enablePush()
        setPerm(pushPermission())
        if (r.ok) {
          setEnabled(true)
        } else if (r.reason === 'denied') {
          setMsg('Notifications are blocked — enable them for this site in your browser settings, then try again.')
        } else if (r.reason === 'not-configured') {
          setMsg('Push isn’t set up on the server yet. It’ll work once VAPID keys are configured.')
        } else if (r.reason === 'unsupported') {
          setMsg('This browser doesn’t support push notifications.')
        } else {
          setMsg('Couldn’t enable notifications.')
        }
      }
    } catch {
      setMsg('Something went wrong enabling notifications.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Push Notifications" subtitle="Free browser/phone alerts for new orders and frost warnings — no email needed">
      {!supported ? (
        <div style={{ fontSize: 12.5, color: C.muted }}>
          This browser doesn’t support push notifications. On iPhone, first <strong>Add Grafty to your Home Screen</strong>
          (Share → Add to Home Screen), open it from there, then enable notifications here.
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>Notifications on this device</div>
              <div style={{ fontSize: 12, color: C.muted }}>
                Delivers even when Grafty is closed. New storefront orders + frost warnings today; more soon.
              </div>
            </div>
            <button
              role="switch" aria-checked={enabled} disabled={busy || perm === 'denied'}
              onClick={toggle}
              style={{
                width: 42, height: 24, borderRadius: 12, border: 'none',
                cursor: busy || perm === 'denied' ? 'not-allowed' : 'pointer',
                background: enabled ? C.amber : 'rgba(255,255,255,0.12)',
                position: 'relative', transition: 'background 0.15s', flexShrink: 0, marginLeft: 16, opacity: busy ? 0.6 : 1,
              }}
            >
              <div style={{ position: 'absolute', top: 4, left: enabled ? 20 : 4, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
            </button>
          </div>
          {perm === 'denied' && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>
              Notifications are blocked for this site. Enable them in your browser’s site settings, then reload.
            </div>
          )}
          {msg && <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>{msg}</div>}
        </div>
      )}
    </Card>
  )
}

// ── Calendar subscription (.ics) ────────────────────────────────────────────────

function CalendarSubscribeCard() {
  const { data, isLoading } = useQuery({ queryKey: ['calendar-subscription'], queryFn: () => calendarApi.subscription().then(r => r.data) })
  const [copied, setCopied] = useState(false)
  const url = data?.url || ''
  const webcal = data?.webcal || url
  // Google Calendar "add by URL" deep link.
  const googleUrl = url ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(url)}` : ''

  function copy() {
    if (!url) return
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Card title="Subscribe to your farm calendar" subtitle="Add upcoming care, tasks, and harvest records to Google Calendar, Apple Calendar, or any app that supports calendar subscriptions (.ics)">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: C.muted, fontSize: 12 }}>
        <Calendar size={14} /> The feed refreshes automatically (about twice a day). Anyone with this link can view your farm schedule — keep it private.
      </div>
      {isLoading ? (
        <div style={{ fontSize: 13, color: C.muted }}>Loading your subscription link…</div>
      ) : !url ? (
        <div style={{ fontSize: 13, color: C.muted }}>Calendar subscription is unavailable right now.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input
              readOnly value={url} onFocus={e => e.currentTarget.select()}
              style={{
                flex: 1, padding: '9px 11px', fontSize: 12, borderRadius: 8,
                border: `1px solid ${C.border}`, background: 'var(--color-bg)', color: C.text,
                fontFamily: 'monospace',
              }}
            />
            <button onClick={copy} style={btnStyle('ghost')}>
              {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <a href={googleUrl} target="_blank" rel="noopener noreferrer" style={{ ...btnStyle('primary'), textDecoration: 'none' }}>
              <Calendar size={14} /> Add to Google Calendar
            </a>
            <a href={webcal} style={{ ...btnStyle('ghost'), textDecoration: 'none' }}>
              <Calendar size={14} /> Add to Apple Calendar
            </a>
            <a href={url} download="grafty-farm.ics" style={{ ...btnStyle('ghost'), textDecoration: 'none' }}>
              <Download size={14} /> Download .ics
            </a>
          </div>
        </>
      )}
    </Card>
  )
}

// ── AI Tab ────────────────────────────────────────────────────────────────────

function AITab() {
  return (
    <div>
      <Card
        title="AI Feature Consent"
        subtitle="Manage your consent for Grafty AI features. You can withdraw consent at any time — this disables AI features but does not delete previously generated outputs."
      >
        {/* Info banner */}
        <div style={{
          padding: '12px 14px', background: 'color-mix(in srgb, var(--color-primary) 7%, transparent)',
          border: '1px solid color-mix(in srgb, var(--color-primary) 18%, transparent)', borderRadius: 9, marginBottom: 18,
          fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.6,
        }}>
          <strong style={{ color: 'var(--color-primary)' }}>Your data, your choice.</strong>{' '}
          One agreement covers all Grafty AI features (processed with Google Gemini,
          required under GDPR Art. 7). Accept it once below, then turn individual
          capabilities on or off — no separate sign-off per feature.
        </div>

        <ConsentStatusBadge consentType="ai_general" label="Grafty AI (master agreement)" />
        {/* BYOK keeps its own acceptance — distinct billing-responsibility terms. */}
        <ConsentStatusBadge consentType="ai_byok" label="Bring Your Own GCP Project (billing terms)" />
      </Card>

      <Card title="AI Features" subtitle="Turn individual AI capabilities on or off. All are covered by the master agreement above.">
        <AIFeatureToggles />
      </Card>

      <Card title="AI Usage This Month" subtitle="Estimated cost of platform AI features used by your farm this calendar month.">
        <AIBudgetCard />
        <div style={{ height: 18 }} />
        <AIUsageCard />
      </Card>

      <Card title="Privacy Rights" subtitle="Your rights under GDPR, CCPA, and equivalent laws">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { label: 'Access your data',    desc: 'Request a full export of all data Grafty holds about you', action: 'Request Export' },
            { label: 'Delete your account', desc: 'Permanently remove your personal information from Grafty',  action: 'Request Deletion', danger: true },
          ].map(({ label, desc, action, danger }) => (
            <div key={label} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{desc}</div>
              </div>
              <button
                onClick={() => api.post(danger ? '/privacy/my/request?type=deletion' : '/privacy/my/request').catch(() => alert('Privacy request submitted. You will receive an email confirmation.'))}
                style={{
                  padding: '7px 14px', borderRadius: 8, border: `1px solid`,
                  borderColor: danger ? 'rgba(248,113,113,0.3)' : 'rgba(255,255,255,0.1)',
                  background: danger ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.04)',
                  color: danger ? '#F87171' : 'var(--color-text-muted)',
                  cursor: 'pointer', fontSize: 12, fontWeight: 500,
                  flexShrink: 0, marginLeft: 16,
                }}
              >
                {action}
              </button>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 12, lineHeight: 1.6 }}>
          Requests are processed within 45 days. For urgent requests or questions, contact{' '}
          <a href="mailto:privacy@grafty.io" style={{ color: '#F5A623', textDecoration: 'none' }}>privacy@grafty.io</a>.
        </p>
      </Card>

      <Card title="AI Usage History" subtitle="Consent events and consent version history for your account">
        <div style={{ fontSize: 13, color: 'var(--color-text-muted)', padding: '8px 0' }}>
          Consent history is available via{' '}
          <code style={{ fontSize: 12, background: 'var(--color-border)', padding: '2px 6px', borderRadius: 4, color: 'var(--color-text)' }}>
            GET /api/v1/ai/consent/history
          </code>
          . A UI view is coming in a future update.
        </div>
      </Card>
    </div>
  )
}

// ── AI per-feature toggles (feature #5) ─────────────────────────────────────

interface AIFeatureRow { key: string; label: string; description: string; enabled: boolean }

const AI_FEATURE_LABELS: Record<string, string> = {
  ai_yield_analysis: 'Yield analysis',
  ai_weather_analysis: 'Weather analysis',
  ai_ripening_predict: 'Ripening outlook',
  ai_planting_advice: 'Planting recommendations',
  ai_plant_diagnosis: 'Photo diagnosis',
  ai_tree_detection: 'Tree detection',
  ai_receipt_scanning: 'Receipt scanning',
  ai_doc_import: 'Document import',
  ai_activity_parse: 'Activity import',
  ai_labor_insights: 'Labor insights',
  ai_price_estimate: 'Price estimates',
  ai_irrigation_estimate: 'Irrigation estimate',
}

function AIBudgetCard() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['ai-budget'],
    queryFn: () => aiInsightsApi.getBudget().then(r => r.data),
  })
  const [editing, setEditing] = useState(false)
  const [budget, setBudget] = useState('')
  const [threshold, setThreshold] = useState('')
  const save = useMutation({
    mutationFn: () => aiInsightsApi.setBudget(parseFloat(budget) || 0, parseFloat(threshold) || 0),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-budget'] })
      setEditing(false)
    },
  })
  if (isLoading || !data) return null

  const spent = data.month_to_date_usd
  const cap = data.monthly_budget_usd > 0 ? data.monthly_budget_usd : 0
  const pct = cap > 0 ? Math.min(100, (spent / cap) * 100) : 0
  const barColor = data.over_budget ? 'var(--color-danger, #DC2626)'
    : data.over_threshold ? 'var(--color-warning, #D97706)'
      : 'var(--color-primary)'

  const startEdit = () => {
    setBudget(data.monthly_budget_usd ? String(data.monthly_budget_usd) : '')
    setThreshold(data.alert_threshold_usd ? String(data.alert_threshold_usd) : '')
    setEditing(true)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--color-text)' }}>
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>${spent.toFixed(2)}</strong>
          {cap > 0 && <span style={{ color: 'var(--color-text-faint)' }}> of ${cap.toFixed(2)} budget</span>}
          {cap === 0 && <span style={{ color: 'var(--color-text-faint)' }}> spent (no monthly cap)</span>}
        </span>
        {!editing && (
          <button onClick={startEdit} style={{ fontSize: 12, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            Edit budget
          </button>
        )}
      </div>
      {cap > 0 && (
        <div style={{ height: 8, borderRadius: 4, background: 'var(--color-border-tertiary)', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: barColor, transition: 'width 0.3s' }} />
        </div>
      )}
      {(data.over_budget || data.over_threshold) && (
        <div style={{ fontSize: 12, color: barColor, marginTop: 6 }}>
          {data.over_budget
            ? `Over your monthly budget. AI keeps working — this is a soft cap.`
            : `Past your $${data.alert_threshold_usd.toFixed(2)} alert threshold for this month.`}
        </div>
      )}
      {editing && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            <div style={{ marginBottom: 4 }}>Monthly budget ($, 0 = none)</div>
            <input type="number" min="0" step="1" value={budget} onChange={e => setBudget(e.target.value)}
              style={{ width: 120, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text)' }} />
          </label>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            <div style={{ marginBottom: 4 }}>Alert at ($, 0 = none)</div>
            <input type="number" min="0" step="1" value={threshold} onChange={e => setThreshold(e.target.value)}
              style={{ width: 120, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text)' }} />
          </label>
          <button onClick={() => save.mutate()} disabled={save.isPending}
            style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} style={{ padding: '7px 10px', borderRadius: 6, border: 'none', background: 'none', color: 'var(--color-text-muted)', fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 10 }}>
        We email the farm owner once monthly spend crosses the alert threshold. This is a soft cap — AI is never hard-stopped.
      </div>
    </div>
  )
}

function AIUsageCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['ai-usage'],
    queryFn: () => aiInsightsApi.usage().then(r => r.data),
  })
  if (isLoading) return <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading…</div>
  if (!data || data.total_calls === 0) {
    return <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>No AI usage yet this month.</div>
  }
  return (
    <div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Estimated cost</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)' }}>${data.month_usd.toFixed(2)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Requests</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)' }}>{data.total_calls}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.by_feature.map(f => (
          <div key={f.feature} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--color-text)', borderTop: '0.5px solid var(--color-border-tertiary)', paddingTop: 6 }}>
            <span>{AI_FEATURE_LABELS[f.feature] || f.feature} <span style={{ color: 'var(--color-text-faint)' }}>· {f.calls}×</span></span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>${f.cost_usd.toFixed(2)}</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 12 }}>{data.disclaimer}</div>
    </div>
  )
}

function AIFeatureToggles() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['ai-features'],
    queryFn: () => api.get<{ features: AIFeatureRow[] }>('/ai/consent/features').then(r => r.data.features),
  })
  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.put(`/ai/consent/features/${key}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-features'] }),
  })

  if (isLoading) return <div style={{ fontSize: 13, color: 'var(--color-text-muted)', padding: '8px 0' }}>Loading…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {(data ?? []).map(f => (
        <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }}>{f.label}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{f.description}</div>
          </div>
          <button
            role="switch" aria-checked={f.enabled} aria-label={`${f.label} ${f.enabled ? 'on' : 'off'}`}
            disabled={toggle.isPending}
            onClick={() => toggle.mutate({ key: f.key, enabled: !f.enabled })}
            style={{
              flexShrink: 0, width: 42, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer', position: 'relative',
              background: f.enabled ? '#2D6A2F' : 'rgba(255,255,255,0.14)', transition: 'background 0.15s',
            }}>
            <span style={{ position: 'absolute', top: 3, left: f.enabled ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
          </button>
        </div>
      ))}
      <p style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 12, lineHeight: 1.6 }}>
        Turning a feature off blocks Grafty from using AI for that capability tenant-wide,
        even if your plan includes it. Some features also depend on your subscription tier.
      </p>
    </div>
  )
}

// ── Security Tab ──────────────────────────────────────────────────────────────

function SecurityTab() {
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' })

  const changePw = useMutation({
    mutationFn: () => api.post('/me/change-password', {
      current_password: pwForm.current_password,
      new_password: pwForm.new_password,
    }),
    onSuccess: () => setPwForm({ current_password: '', new_password: '', confirm: '' }),
  })

  const { data: sessions = [] } = useQuery<SessionInfo[]>({
    queryKey: ['sessions'],
    queryFn: () => api.get('/me/sessions').then(r => (r.data ?? []) as SessionInfo[]).catch(() => []),
  })

  const revokeSession = useMutation({
    mutationFn: (id: string) => api.delete(`/me/sessions/${id}`),
  })

  const pwMismatch = !!pwForm.new_password && !!pwForm.confirm && pwForm.new_password !== pwForm.confirm

  const pwInputStyle = { ...inputStyle }

  return (
    <div>
      <Card title="Change Password">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
          {[
            { field: 'current_password' as const, label: 'Current Password' },
            { field: 'new_password' as const,     label: 'New Password'     },
            { field: 'confirm' as const,           label: 'Confirm New Password' },
          ].map(({ field, label }) => (
            <div key={field}>
              <label style={labelStyle}>{label}</label>
              <input
                type="password" style={pwInputStyle}
                value={pwForm[field]}
                onChange={e => setPwForm(f => ({ ...f, [field]: e.target.value }))}
              />
            </div>
          ))}
          {pwMismatch && (
            <p style={{ fontSize: 12, color: C.danger, margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <AlertCircle size={12} /> Passwords do not match
            </p>
          )}
        </div>
        <SaveRow
          isPending={changePw.isPending}
          isSuccess={changePw.isSuccess}
          isError={changePw.isError}
          onSave={() => changePw.mutate()}
          label="Update Password"
        />
      </Card>

      <Card title="Two-Factor Authentication" subtitle="Add an extra layer of security to your account">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, color: C.text, fontWeight: 500, marginBottom: 2 }}>Authenticator App (TOTP)</div>
            <div style={{ fontSize: 12, color: C.muted }}>Works with Google Authenticator, Authy, 1Password, Bitwarden</div>
          </div>
          <button
            style={btnStyle('ghost')}
            onClick={() => api.post('/me/totp/setup')
              .then(r => alert('Scan this URL in your authenticator app:\n\n' + r.data?.qr_code_url))
              .catch(() => alert('TOTP setup endpoint not available yet.'))}
          >
            <Key size={13} />
            Set Up TOTP
          </button>
        </div>
      </Card>

      {sessions.length > 0 && (
        <Card title="Active Sessions" subtitle="Devices currently signed in to your account">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sessions.map((s) => (
              <div key={s.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px', background: C.card2, borderRadius: 8,
              }}>
                <div>
                  <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{s.user_agent || 'Unknown device'}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {s.ip_address} · Last active {new Date(s.last_used_at).toLocaleDateString()}
                  </div>
                </div>
                {s.is_current
                  ? <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>● This device</span>
                  : <button style={{ ...btnStyle('danger'), padding: '6px 12px', fontSize: 12 }} onClick={() => revokeSession.mutate(s.id)}>Revoke</button>
                }
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
